import React, { useState, useRef, useEffect } from 'react';
import { cn } from '../../lib/utils';
import { Input } from './input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select';

export type NumberInputUnitOption = { value: string; label: string };

interface NumberInputProps {
  id?: string;
  value: string | number;
  onChange: (value: string) => void;
  onFocus?: () => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  placeholder?: string;
  className?: string;
  min?: number;
  step?: number;
  disabled?: boolean;
  max?: number;
  /** When true, unit dropdown is shown beside the input. Default false (hidden). */
  showUnitSelect?: boolean;
  unitValue?: string;
  unitOptions?: NumberInputUnitOption[];
  onUnitChange?: (value: string) => void;
  /** Classes for the horizontal row wrapper when showUnitSelect is true (e.g. mt-2). */
  unitSelectWrapperClassName?: string;
}

export const NumberInput: React.FC<NumberInputProps> = ({
  id,
  value,
  onChange,
  onFocus,
  onBlur,
  placeholder,
  className,
  min,
  step = 0.01,
  disabled,
  max,
  showUnitSelect = false,
  unitValue,
  unitOptions,
  onUnitChange,
  unitSelectWrapperClassName,
}) => {
  const [displayValue, setDisplayValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Only show value if it's not 0 or empty, otherwise show empty to display placeholder
    if (value && value !== 0 && value !== '0' && value !== '') {
      setDisplayValue(value.toString());
    } else {
      setDisplayValue('');
    }
  }, [value]);

  const normalizeNumber = (inputValue: string): string => {
    // If input is empty, return empty string
    if (inputValue === '' || inputValue === null || inputValue === undefined) {
      return '';
    }

    // Handle decimal numbers - preserve leading zero for decimals like 0.5
    if (inputValue.includes('.')) {
      const parts = inputValue.split('.');
      const wholePart = parts[0];
      const decimalPart = parts[1];

      // Remove leading zeros from whole part but keep single zero
      const normalizedWhole = wholePart.replace(/^0+/, '') || '0';

      return `${normalizedWhole}.${decimalPart}`;
    }

    // For whole numbers, remove leading zeros but keep single zero
    const trimmed = inputValue.replace(/^0+/, '');
    return trimmed === '' ? '0' : trimmed;
  };

  const handleFocus = () => {
    // Only normalize if there's actually a value to normalize
    if (displayValue && displayValue.trim() !== '') {
      const normalized = normalizeNumber(displayValue);
      setDisplayValue(normalized);
    }
    onFocus?.();
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Only normalize if there's actually a value to normalize
    if (displayValue && displayValue.trim() !== '') {
      const normalized = normalizeNumber(displayValue);
      setDisplayValue(normalized);
    }
    onBlur?.(e);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    // If step is 1, don't allow decimal points
    if (step === 1 && newValue.includes('.')) {
      return; // Keep the old value, don't update
    }

    setDisplayValue(newValue);
    onChange(newValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // If step is 1, prevent decimal point key
    if (step === 1 && e.key === '.') {
      e.preventDefault();
    }
  };

  const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
    // Prevent mouse wheel from changing the number input value
    e.preventDefault();
  };

  const unitRowActive =
    showUnitSelect &&
    !!unitOptions?.length;
  const selectedUnitValue = unitValue || unitOptions?.[0]?.value;
  const hasError = typeof className === 'string' && className.includes('border-red');

  const inputEl = (
    <Input
      ref={inputRef}
      id={id}
      type="number"
      min={min}
      step={step}
      max={max}
      value={displayValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onWheel={handleWheel}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={placeholder}
      className={cn(
        unitRowActive &&
          'h-10 min-h-10 min-w-0 w-full rounded-none border-0 border-r border-input bg-transparent py-0 leading-10 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 [-moz-appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
        className
      )}
      disabled={disabled}
    />
  );

  if (!unitRowActive) {
    return inputEl;
  }

  return (
    <div
      className={cn(
        'grid h-10 w-full min-h-10 grid-cols-[minmax(0,70%)_minmax(90px,30%)] items-stretch gap-0 overflow-visible rounded-lg border bg-white ring-offset-background transition-colors',
        hasError ? 'border-readable-red' : 'border-[#E2E5EA] hover:border-heritage-grey',
        'focus-within:border-hero-red focus-within:ring-2 focus-within:ring-hero-red/30',
        unitSelectWrapperClassName
      )}
    >
      {inputEl}
      <Select
        value={selectedUnitValue}
        onValueChange={(value) => onUnitChange?.(value)}
        disabled={disabled}
      >
        <SelectTrigger className="h-10 min-h-10 w-full rounded-none border-0 border-l border-[#E2E5EA] bg-transparent px-2 text-xs shadow-none focus:ring-0 focus:ring-offset-0 data-[state=open]:ring-0">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {unitOptions.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};
